import { useEffect, useMemo, useState } from "react";
import { RefreshCw } from "lucide-react";
import api from "@/lib/api";
import MailTemplateModal from "@/components/MailTemplateModal";
import MovimentiCollegatiModal from "@/components/MovimentiCollegatiModal";
import AlunnoLogModal from "@/components/AlunnoLogModal";
import VariazioneBadges from "@/components/VariazioneBadges";

const DAYS_LABELS = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì"];

const fmtAnnoScol = (a) => {
  const n = Number(a);
  if (!n) return "—";
  return `${n}/${String(n + 1).slice(-2)}`;
};
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
};
const fmtImporto = (v) => {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const settoreLabel = (s) => {
  const u = String(s || "").toUpperCase();
  if (u === "A") return "A · Infanzia";
  if (u === "E") return "E · Primaria E";
  if (u === "F") return "F · Primaria F";
  if (u === "M") return "M · Secondaria";
  return u || "—";
};

// Numero classe → gruppo doposcuola: 1-2 → gruppo 1, 3-4-5 → gruppo 2
function classToGruppo(classe) {
  const m = /^\s*(\d+)/.exec(String(classe || ""));
  if (!m) return null;
  const n = Number(m[1]);
  if (n >= 1 && n <= 2) return 1;
  if (n >= 3 && n <= 5) return 2;
  return null;
}

// Normalizza il valore "giorni" di un'iscrizione in un Set di weekday indici 0-4 (Lun-Ven).
function giorniToSet(g) {
  const out = new Set();
  if (Array.isArray(g)) {
    for (const v of g) {
      if (v == null) continue;
      const n = Number(v);
      if (Number.isFinite(n) && n >= 0 && n <= 4) out.add(n);
    }
  } else if (g != null && g !== "") {
    // potrebbe essere stringa "0,2,4" o singolo "2"
    String(g).split(/[,;\s]+/).forEach((tok) => {
      const n = Number(tok);
      if (Number.isFinite(n) && n >= 0 && n <= 4) out.add(n);
    });
  }
  return out;
}

export default function ExtraIscrizioniCronoView({ onNewIscrizione, onOpenCorso }) {
  const [items, setItems] = useState([]);
  const [movimentiCounts, setMovimentiCounts] = useState({});      // { iid: count }
  const [movimentiModal, setMovimentiModal] = useState(null);      // { iscr, data } | null
  const [loadingMov, setLoadingMov] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [search, setSearch] = useState("");
  const [filterAnno, setFilterAnno] = useState("");
  const [filterCorso, setFilterCorso] = useState("");
  const [filterGiorno, setFilterGiorno] = useState("");
  const [filterGruppo, setFilterGruppo] = useState("");
  const [sortDir, setSortDir] = useState("desc");
  const [filterStato, setFilterStato] = useState("");
  const [mailModalIscr, setMailModalIscr] = useState(null);
  const [mailModalTpl, setMailModalTpl] = useState(null);
  // 🆕 Uniformato con "Corsi ›› Alunni": popup recesso + modal storico alunno
  const [recessoModal, setRecessoModal] = useState(null); // { it, value } | null
  const [logAlunno, setLogAlunno] = useState(null); // alunno_cod | null
  const [editingPosId, setEditingPosId] = useState(null);
  const [editingPosVal, setEditingPosVal] = useState("");
  const [savingPos, setSavingPos] = useState(false);

  const apriPosEdit = (it, currentPos) => {
    setEditingPosId(it._id);
    setEditingPosVal(it.pos_override != null ? String(it.pos_override) : String(currentPos || ""));
  };
  const chiudiPosEdit = () => { setEditingPosId(null); setEditingPosVal(""); };
  const salvaPosOverride = async (it) => {
    setSavingPos(true);
    try {
      const v = (editingPosVal ?? "").trim();
      const payload = v === "" ? { pos_override: null } : { pos_override: parseInt(v, 10) };
      if (v !== "" && (!Number.isInteger(payload.pos_override) || payload.pos_override <= 0)) {
        alert("Inserisci un intero positivo, oppure svuota il campo per tornare alla posizione automatica.");
        setSavingPos(false); return;
      }
      await api.put(`/mensa-matrice/extra-iscrizioni/${it._id}`, payload);
      await load();
      chiudiPosEdit();
    } catch (e) {
      alert(e.response?.data?.detail || "Errore salvataggio POS");
    } finally { setSavingPos(false); }
  };

  // 🆕 Palette colori badge stato: spettro più ampio per distinguerli a colpo d'occhio
  // (prima alcuni stati avevano sfumature di blu/verde troppo simili).
  const STATO_META = {
    pending:                { bg: "#1976d2", txt: "● IN ATTESA" },            // blu medio
    waiting:                { bg: "#ef6c00", txt: "⏳ IN LISTA D'ATTESA" },    // arancione
    in_attesa_pagamento:    { bg: "#7b1fa2", txt: "💳 INVIATO LINK PAG." },   // viola
    accettata_mail_inviata: { bg: "#ad1457", txt: "📨 ACCETTATA (MAIL INVIATA)" }, // magenta scuro (distinto)
    confermata_genitore:    { bg: "#ff8f00", txt: "✓✓ CONFERMATA GENITORE" }, // ambra
    confirmed:              { bg: "#388e3c", txt: "✓ APPROVATA" },            // verde
    pagata_da_verificare:   { bg: "#00838f", txt: "🔍 DA VERIFICARE" },       // ciano (distinto dai blu)
    pagata:                 { bg: "#1b5e20", txt: "💳 CONFERMATA PAGATA" },   // verde scuro
    rejected:               { bg: "#424242", txt: "✗ RIFIUTATA" },            // grigio scuro
    scaduta:                { bg: "#5d4037", txt: "⏰ SCADUTA" },              // marrone
    recesso:                { bg: "#c62828", txt: "⊘ RECESSO" },               // rosso
    chiusa:                 { bg: "#455a64", txt: "🔒 CHIUSA" },              // slate
  };
  const statoOf = (it) => it.stato_iscrizione || ((it.data_recesso || "").trim() ? "recesso" : (it.in_lista_attesa ? "waiting" : "pending"));

  const setStato = async (it, newStato, label) => {
    if (!window.confirm(`${label} l'iscrizione di ${it.cognome} ${it.nome} a "${it.corso_descrizione || it.corso_codice}"?`)) return;
    try { await api.put(`/mensa-matrice/extra-iscrizioni/${it._id}`, { stato_iscrizione: newStato }); await load(); }
    catch (e) { setErr(e.response?.data?.detail || "Errore"); }
  };
  const apriMailTemplate = (it, tpl) => { setMailModalIscr(it); setMailModalTpl(tpl); };
  const chiudiMailModal = () => { setMailModalIscr(null); setMailModalTpl(null); };

  const verificaEMarcaPagata = async (it) => {
    if (!window.confirm(`✓ Hai verificato la contabile di ${it.cognome} ${it.nome} per "${it.corso_descrizione || it.corso_codice}"?\n\nLo stato passerà da "DA VERIFICARE" a "PAGATA" (senza inviare email — usa il tasto "✅ Pagato" se vuoi inviare anche la mail).`)) return;
    try {
      await api.post(`/mensa-matrice/extra-iscrizioni/${it._id}/conferma-pagamento`);
      await load();
    } catch (e) { setErr(e.response?.data?.detail || "Errore conferma pagamento"); }
  };

  // ✅ Pagato → consente di scegliere se inviare anche la mail al genitore.
  // OK    → apre l'editor mail (template conferma_pagamento)
  // Annulla → marca direttamente stato = "pagata" senza mail
  const pagatoConOSenzaMail = async (it) => {
    const choice = window.confirm(
      `✅ Marca come PAGATA l'iscrizione di ${it.cognome} ${it.nome} a "${it.corso_descrizione || it.corso_codice}".\n\n` +
      `Vuoi anche INVIARE la mail di conferma al genitore?\n\n` +
      `• OK → apri editor mail e invia\n` +
      `• Annulla → marca solo come PAGATA, senza inviare alcuna mail`
    );
    if (choice) {
      apriMailTemplate(it, "conferma_pagamento");
    } else {
      try {
        await api.post(`/mensa-matrice/extra-iscrizioni/${it._id}/conferma-pagamento`);
        await load();
      } catch (e) { setErr(e.response?.data?.detail || "Errore conferma pagamento"); }
    }
  };
  const eliminaIscr = async (it) => {
    if (!window.confirm(`Eliminare definitivamente l'iscrizione di ${it.cognome} ${it.nome} a "${it.corso_descrizione || it.corso_codice}"?`)) return;
    try { await api.delete(`/mensa-matrice/extra-iscrizioni/${it._id}`); await load(); }
    catch (e) { setErr(e.response?.data?.detail || "Errore eliminazione"); }
  };

  // 💰 Apre modale con i movimenti contabili collegati a un'iscrizione
  // (match: causale corso + alunno + anno competenza).
  const apriMovimenti = async (it) => {
    setLoadingMov(true);
    try {
      const r = await api.get(`/mensa-matrice/extra-iscrizioni/${it._id}/movimenti-collegati`);
      setMovimentiModal({ iscr: it, data: r.data });
    } catch (e) {
      alert(e.response?.data?.detail || "Errore caricamento movimenti");
    } finally { setLoadingMov(false); }
  };

  // 📤 Upload contabile da parte dell'ADMIN (segreteria). Apre un file picker
  // nascosto e invia il file all'endpoint admin che imposta direttamente lo
  // stato a "pagata" (la segreteria sa già che la contabile è valida) e
  // registra data/ora/utente dell'upload.
  const uploadContabileAdmin = async (it) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "application/pdf,image/jpeg,image/jpg,image/png,image/webp";
    input.style.display = "none";
    document.body.appendChild(input);
    const result = await new Promise((resolve) => {
      input.onchange = () => resolve(input.files && input.files[0] ? input.files[0] : null);
      input.click();
      // safety: se l'utente chiude senza scegliere, dopo 60s resolve null
      setTimeout(() => resolve(null), 60000);
    });
    document.body.removeChild(input);
    if (!result) return;

    const sizeKb = (result.size / 1024).toFixed(0);
    const nominativo = `${it.cognome} ${it.nome}`.trim();
    const corso = it.corso_descrizione || it.corso_codice;
    const warn = it.has_contabile
      ? `⚠ ATTENZIONE: questa iscrizione ha GIÀ una contabile caricata (${it.contabile_filename || "—"} del ${fmtDateTime(it.contabile_uploaded_at)}). Il file verrà SOSTITUITO.\n\n`
      : "";
    if (!window.confirm(
      `📤 Carica contabile pagamento per ${nominativo} · ${corso}\n\n` +
      warn +
      `File: ${result.name} · ${sizeKb} KB · ${result.type || "?"}\n\n` +
      `Lo stato passerà direttamente a "💳 CONFERMATA PAGATA" e verrà registrato il tuo nome utente come uploader.\n\nConfermi?`
    )) return;

    try {
      const fd = new FormData();
      fd.append("file", result);
      const r = await api.post(`/mensa-matrice/extra-iscrizioni/${it._id}/contabile`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const d = r.data || {};
      alert(
        `✓ Contabile caricata.\n\n` +
        `File: ${d.contabile_filename}\n` +
        `Data/ora upload: ${fmtDateTime(d.contabile_uploaded_at)}\n` +
        `Caricata da: ${d.contabile_uploaded_by}\n` +
        `Nuovo stato: ${d.stato_iscrizione}`
      );
      await load();
    } catch (e) {
      alert(`Errore upload: ${e.response?.data?.detail || e.message}`);
    }
  };

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await api.get("/mensa-matrice/extra-iscrizioni");
      const its = r.data?.items || [];
      setItems(its);
      // 🆕 Fetch movimenti counts in parallelo (non-blocking)
      if (its.length > 0) {
        api.post("/mensa-matrice/extra-iscrizioni/movimenti-counts", { ids: its.map(x => x._id) })
          .then(r2 => setMovimentiCounts(r2.data?.counts || {}))
          .catch(() => setMovimentiCounts({}));
      } else {
        setMovimentiCounts({});
      }
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // All'apertura, leggi l'anno scolastico forzato dalle Impostazioni Extra e usalo
  // come filtro di default. Se non impostato, lascia "tutti gli anni".
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/mensa-matrice/iscrizioni-config");
        const a = r.data?.anno_iscrizioni;
        if (a != null) { setAnnoConfig(a); setFilterAnno(String(a)); }
      } catch { /* ignore */ }
    })();
  }, []);

  // 🥇 Calcola posizione e totale per ogni iscrizione all'interno del proprio corso
  // (corso_codice + anno), ordinando per created_at ASC. Esclude iscrizioni in
  // lista d'attesa dal conteggio "attivi": la posizione include comunque tutte
  // le iscrizioni (in_lista_attesa + attive) per riflettere l'ordine reale di arrivo.
  const positionByItemId = useMemo(() => {
    const groups = new Map(); // "corso|anno" → [items asc per created_at]
    for (const it of items) {
      const key = `${it.corso_codice || ""}|${it.anno ?? ""}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(it);
    }
    const map = new Map();
    for (const [, arr] of groups) {
      arr.sort((a, b) => {
        const ta = a.created_at ? Date.parse(a.created_at) : 0;
        const tb = b.created_at ? Date.parse(b.created_at) : 0;
        return ta - tb;
      });
      arr.forEach((it, idx) => {
        if (!it._id) return;
        const autoPos = idx + 1;
        const override = (it.pos_override != null && Number.isInteger(it.pos_override) && it.pos_override > 0) ? it.pos_override : null;
        map.set(it._id, {
          pos: override != null ? override : autoPos,
          total: arr.length,
          auto: autoPos,
          isOverride: override != null,
        });
      });
    }
    return map;
  }, [items]);

  // Anno scolastico forzato dalle Impostazioni Extra (es. 2026/27).
  // Mantenuto in state separato per essere incluso anche se non ci sono ancora iscrizioni.
  const [annoConfig, setAnnoConfig] = useState(null);
  const anniDisponibili = useMemo(() => {
    const s = new Set();
    for (const it of items) if (it.anno != null) s.add(it.anno);
    if (annoConfig != null) s.add(Number(annoConfig));
    return [...s].sort((a, b) => Number(b) - Number(a));
  }, [items, annoConfig]);

  const corsiDisponibili = useMemo(() => {
    // Mappa codice → descrizione (preferisci corso_descrizione, fallback attivita)
    const m = new Map();
    for (const it of items) {
      if (!it.corso_codice) continue;
      if (!m.has(it.corso_codice)) {
        m.set(it.corso_codice, it.corso_descrizione || it.attivita || it.corso_codice);
      }
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [items]);

  const giorniDisponibili = useMemo(() => {
    const s = new Set();
    for (const it of items) for (const g of giorniToSet(it.giorni)) s.add(g);
    return [...s].sort((a, b) => a - b);
  }, [items]);

  const filtered = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    const giornoNum = filterGiorno === "" ? null : Number(filterGiorno);
    const gruppoNum = filterGruppo === "" ? null : Number(filterGruppo);
    const arr = items.filter((it) => {
      if (filterAnno && String(it.anno) !== String(filterAnno)) return false;
      if (filterCorso && String(it.corso_codice || "") !== filterCorso) return false;
      if (filterStato) {
        if (filterStato === "variazione") { if (!it.variata) return false; }
        else if (statoOf(it) !== filterStato) return false;
      }
      if (giornoNum != null) {
        const set = giorniToSet(it.giorni);
        if (!set.has(giornoNum)) return false;
      }
      if (gruppoNum != null) {
        const g = classToGruppo(it.classe);
        if (g !== gruppoNum) return false;
      }
      if (q) {
        const hay = [
          it.cognome, it.nome, it.alunno_cod, it.corso_codice, it.corso_descrizione,
          it.attivita, it.classe,
        ].map(v => String(v || "").toLowerCase()).join(" ");
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    arr.sort((a, b) => {
      const ta = a.created_at ? Date.parse(a.created_at) : 0;
      const tb = b.created_at ? Date.parse(b.created_at) : 0;
      return sortDir === "desc" ? tb - ta : ta - tb;
    });
    return arr;
  }, [items, search, filterAnno, filterCorso, filterGiorno, filterGruppo, filterStato, sortDir]);

  const totalePrezzo = useMemo(
    () => filtered.reduce((s, it) => s + (Number(it.prezzo) || 0), 0),
    [filtered]
  );

  const someFilterOn = !!(filterAnno || filterCorso || filterGiorno !== "" || filterGruppo !== "" || filterStato || search);
  const resetFiltri = () => {
    setFilterAnno(""); setFilterCorso("");
    setFilterGiorno(""); setFilterGruppo(""); setFilterStato(""); setSearch("");
  };

  // 🧾 Apre una nuova finestra con la "LISTA ADDEBITI": elenco delle iscrizioni
  // (rispettando i filtri attualmente attivi) raggruppate per CORSO e
  // ordinate per COGNOME + NOME, con il prezzo del listino da addebitare.
  // Esclude iscrizioni in stati non addebitabili: rejected, recesso, scaduta,
  // waiting (lista d'attesa) — perché non vanno mai fatturate.
  //
  // 🆕 Flusso contabilizzazione:
  //  - Separa le iscrizioni in "DA CONTABILIZZARE" (contabilizzata≠true) e
  //    "GIÀ CONTABILIZZATE" (già incluse in un tabulato precedente).
  //  - Chiede conferma all'utente: se accetta, marca via backend le nuove
  //    iscrizioni come contabilizzate prima di aprire la stampa, così che al
  //    prossimo click compaiano nella sezione "GIÀ CONTABILIZZATE".
  //  - Nella stampa è visibile data/ora stampa, batch_id e separazione visiva
  //    fra le due categorie.
  const stampaListaAddebiti = async () => {
    const esc = (s) => String(s ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#39;" }[c]));
    if (filtered.length === 0) {
      alert("Nessuna iscrizione da stampare con i filtri attuali.");
      return;
    }
    const NON_ADDEBITABILI = new Set(["rejected", "recesso", "scaduta", "waiting"]);
    const billable = filtered.filter(it => !NON_ADDEBITABILI.has(statoOf(it)));
    const daContab = billable.filter(it => it.contabilizzata !== true);

    // Se ci sono iscrizioni addebitabili non ancora contabilizzate, offri (opzionale) di marcarle.
    let marcate = false;
    let batchId = null;
    let batchAt = null;
    if (daContab.length > 0) {
      const msg = `🧾 STAMPA ELENCO ISCRIZIONI\n\n` +
        `Ci sono ${daContab.length} iscrizioni addebitabili non ancora contabilizzate.\n\n` +
        `Vuoi MARCARLE come 'contabilizzate' dopo la stampa?\n\n` +
        `• OK → marca e stampa\n` +
        `• Annulla → stampa soltanto (nessuna modifica)`;
      if (window.confirm(msg)) {
        try {
          const r = await api.post("/mensa-matrice/extra-iscrizioni/_bulk/contabilizza", {
            ids: daContab.map(it => it._id),
          });
          marcate = true;
          batchId = r.data?.batch_id || null;
          batchAt = r.data?.contabilizzata_at || null;
        } catch (e) {
          alert(`Errore durante la contabilizzazione: ${e.response?.data?.detail || e.message}`);
          return;
        }
      }
    }

    // Etichette stato (plain text per la stampa)
    const STATO_TXT = {
      pending: "● Inserita",
      waiting: "⏳ Lista d'attesa",
      in_attesa_pagamento: "💳 Inviato link pag.",
      accettata_mail_inviata: "💳 Inviato link pag.",
      confirmed: "💳 Inviato link pag.",
      confermata_genitore: "💳 Inviato link pag.",
      pagata_da_verificare: "🔍 Da verificare",
      pagata: "✓ Pagata",
      scaduta: "⏰ Scaduta",
      recesso: "⊘ Recesso",
      rejected: "✗ Rifiutata",
      chiusa: "🔒 Chiusa",
    };
    const giorniLbl = (g) => [...giorniToSet(g)].sort((a, b) => a - b).map(d => DAYS_LABELS[d]?.slice(0, 3)).join(", ") || "—";

    const oraStampa = new Date();
    const oraStampaStr = oraStampa.toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
    const annoLabel = filterAnno ? fmtAnnoScol(filterAnno) : "tutti gli anni";

    // ── TABULATO "Lista Addebiti" — albero per TIPO CORSO ──
    // Corsi normali (no DOPO*): 1 pagina per CORSO, sottosezioni per giorno.
    // Doposcuola MEDIE (DOPO_M*): 1 pagina per GIORNO.
    // Doposcuola PRIMARIA (DOPO_EF*): 1 pagina per GIORNO + GRUPPO.
    // Alunni ordinati per data/ora iscrizione crescente (primo iscritto in cima).
    // Un'iscrizione con più giorni compare una volta per ciascun giorno.
    const gruppoLbl = (g) => g == null ? "—" : String(g);
    const codeOf = (it) => String(it.corso_codice || "").toUpperCase();
    const isMedie = (it) => codeOf(it).startsWith("DOPO_M");
    const isDopo = (it) => codeOf(it).startsWith("DOPO");
    const isPrimaria = (it) => isDopo(it) && !isMedie(it);
    const byDataAsc = (a, b) => {
      const ta = a.created_at ? Date.parse(a.created_at) : 0;
      const tb = b.created_at ? Date.parse(b.created_at) : 0;
      return ta - tb;
    };
    const DAY_SEQ = [0, 1, 2, 3, 4, -1];
    const daysOf = (it) => { const s = [...giorniToSet(it.giorni)]; return s.length ? s : [-1]; };
    const giornoLabelOf = (d) => d === -1 ? "Nessun giorno" : (DAYS_LABELS[d] || `Giorno ${d}`);

    let totaleImporto = 0, totaleRighe = 0, nSezioni = 0;

    const rowFor = (it, idx) => {
      const prezzo = Number(it.prezzo) || 0;
      const corso = it.corso_descrizione || it.attivita || it.corso_codice || "—";
      const pi = it._id ? positionByItemId.get(it._id) : null;
      const posTxt = pi ? `${pi.pos}${pi.isOverride ? "✎" : ""}` : "—";
      return `<tr>
        <td class="num">${idx + 1}</td>
        <td class="mono" style="text-align:center">${esc(posTxt)}</td>
        <td class="mono">${esc(fmtAnnoScol(it.anno))}</td>
        <td class="mono nowrap">${esc(fmtDateTime(it.created_at))}</td>
        <td class="strong">${esc((it.cognome || "") + " " + (it.nome || ""))}<span class="cod-inline">Cod. ${esc(it.alunno_cod ?? "—")}</span></td>
        <td>${esc(settoreLabel(it.settore))}</td>
        <td>${esc(it.classe || "—")}</td>
        <td style="text-align:center">${esc(gruppoLbl(classToGruppo(it.classe)))}</td>
        <td>${esc(corso)}<span class="giorni-inline">${esc(giorniLbl(it.giorni))}</span></td>
        <td class="price">€ ${esc(fmtImporto(prezzo))}</td>
        <td class="stato">${esc(STATO_TXT[statoOf(it)] || statoOf(it))}</td>
      </tr>`;
    };
    const tableFor = (arr, subLabel) => {
      const sorted = [...arr].sort(byDataAsc);
      let subTot = 0; sorted.forEach(it => subTot += Number(it.prezzo) || 0);
      totaleImporto += subTot; totaleRighe += sorted.length;
      const righe = sorted.map((it, idx) => rowFor(it, idx)).join("");
      return `<table class="tbl">
        <thead><tr><th>N°</th><th>POS.</th><th>ANNO ISCR.</th><th>DATA / ORA</th><th>ALUNNO</th><th>SETT.</th><th>CLASSE</th><th>GRUPPO</th><th>CORSO · GIORNI</th><th>PREZZO €</th><th>STATO</th></tr></thead>
        <tbody>${righe}</tbody>
        <tfoot><tr class="subtot"><td colspan="9" style="text-align:right">SUBTOTALE ${esc(subLabel)} (${sorted.length})</td><td class="price"><strong>€ ${esc(fmtImporto(subTot))}</strong></td><td></td></tr></tfoot>
      </table>`;
    };
    const sezione = (headerHtml, bodyHtml) => { nSezioni++; return `<section class="sec"><div class="sec-hdr">${headerHtml}</div>${bodyHtml}</section>`; };

    const sezioni = [];

    // 1) Corsi normali (no doposcuola) — una pagina per CORSO, sottosezioni per giorno
    const corsiMap = new Map();
    for (const it of filtered.filter(x => !isDopo(x))) {
      const cod = it.corso_codice || "—";
      if (!corsiMap.has(cod)) corsiMap.set(cod, { cod, desc: it.corso_descrizione || it.attivita || cod, items: [] });
      corsiMap.get(cod).items.push(it);
    }
    for (const c of [...corsiMap.values()].sort((a, b) => String(a.desc).localeCompare(String(b.desc), "it", { sensitivity: "base" }))) {
      const perGiorno = new Map();
      for (const it of c.items) for (const d of daysOf(it)) { if (!perGiorno.has(d)) perGiorno.set(d, []); perGiorno.get(d).push(it); }
      const dayBlocks = DAY_SEQ.filter(d => perGiorno.has(d)).map(d =>
        `<div class="day-sub">🗓 ${esc(giornoLabelOf(d))}</div>${tableFor(perGiorno.get(d), `${c.desc} · ${giornoLabelOf(d)}`)}`
      ).join("");
      const hdr = `<span class="sec-corso">📋 ${esc(c.cod)} · ${esc(c.desc)}</span><span class="sec-count">${c.items.length} iscrizioni</span>`;
      sezioni.push(sezione(hdr, dayBlocks));
    }

    // 2) Doposcuola MEDIE (DOPO_M) — una pagina per GIORNO
    const medie = filtered.filter(isMedie);
    const medieDesc = (medie[0] && (medie[0].corso_descrizione || medie[0].attivita)) || "Doposcuola Medie";
    {
      const perGiorno = new Map();
      for (const it of medie) for (const d of daysOf(it)) { if (!perGiorno.has(d)) perGiorno.set(d, []); perGiorno.get(d).push(it); }
      for (const d of DAY_SEQ.filter(x => perGiorno.has(x))) {
        const hdr = `<span class="sec-corso">🏫 ${esc(medieDesc)}</span><span class="sec-giorno">🗓 ${esc(giornoLabelOf(d))}</span><span class="sec-count">${perGiorno.get(d).length} iscrizioni</span>`;
        sezioni.push(sezione(hdr, tableFor(perGiorno.get(d), `${medieDesc} · ${giornoLabelOf(d)}`)));
      }
    }

    // 3) Doposcuola PRIMARIA (DOPO_EF) — una pagina per GIORNO + GRUPPO
    const primaria = filtered.filter(isPrimaria);
    const primDesc = (primaria[0] && (primaria[0].corso_descrizione || primaria[0].attivita)) || "Doposcuola Primaria";
    {
      const perGG = new Map();
      for (const it of primaria) {
        const gr = classToGruppo(it.classe);
        for (const d of daysOf(it)) {
          const key = `${d}||${gr == null ? "x" : gr}`;
          if (!perGG.has(key)) perGG.set(key, { giorno: d, gruppo: gr, items: [] });
          perGG.get(key).items.push(it);
        }
      }
      const ggArr = [...perGG.values()].sort((a, b) => {
        const da = DAY_SEQ.indexOf(a.giorno), db2 = DAY_SEQ.indexOf(b.giorno);
        if (da !== db2) return da - db2;
        return (a.gruppo == null ? 99 : a.gruppo) - (b.gruppo == null ? 99 : b.gruppo);
      });
      for (const g of ggArr) {
        const hdr = `<span class="sec-corso">🏫 ${esc(primDesc)}</span><span class="sec-giorno">🗓 ${esc(giornoLabelOf(g.giorno))}</span><span class="sec-gruppo">👥 Gruppo ${esc(gruppoLbl(g.gruppo))}</span><span class="sec-count">${g.items.length} iscrizioni</span>`;
        sezioni.push(sezione(hdr, tableFor(g.items, `${primDesc} · ${giornoLabelOf(g.giorno)} · Gruppo ${gruppoLbl(g.gruppo)}`)));
      }
    }

    const tabellaCrono = sezioni.join("") || `<div style="padding:20px;color:#999">Nessuna iscrizione da stampare.</div>`;

    const filtriDescr = [];
    if (filterAnno) filtriDescr.push(`A.S. ${fmtAnnoScol(filterAnno)}`);
    if (filterCorso) filtriDescr.push(`Corso ${filterCorso}`);
    if (filterStato) filtriDescr.push(`Stato: ${filterStato}`);
    if (filterGiorno !== "") filtriDescr.push(`Giorno: ${DAYS_LABELS[Number(filterGiorno)]}`);
    if (filterGruppo !== "") filtriDescr.push(`Gruppo ${filterGruppo}`);
    if (search) filtriDescr.push(`Ricerca: "${search}"`);
    const filtriHtml = filtriDescr.length
      ? `<div class="filters">Filtri attivi: ${filtriDescr.map(esc).join(" · ")}</div>`
      : `<div class="filters">Nessun filtro · tutte le iscrizioni</div>`;

    const html = `<!doctype html>
<html lang="it"><head><meta charset="utf-8"><title>Lista Addebiti · ${esc(annoLabel)} · ${esc(oraStampaStr)}</title>
<style>
  @page { size: A4; margin: 10mm 12mm; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; font-family: Arial, Helvetica, sans-serif; color: #222; }
  .doc-hdr { border-bottom: 3px solid #c62828; padding-bottom: 8px; margin-bottom: 10px; }
  .doc-hdr h1 { margin: 0; font-size: 20px; color: #c62828; letter-spacing: 0.5px; }
  .doc-hdr .sub { margin-top: 4px; font-size: 12px; color: #555; }
  .doc-hdr .stamp { display: inline-block; margin-top: 4px; padding: 3px 10px; background: #263238; color: white; font-family: Consolas, monospace; font-size: 12px; font-weight: 700; border-radius: 3px; }
  .filters { margin-top: 4px; font-size: 11px; color: #6a1b9a; font-style: italic; }
  .sec { margin-bottom: 14px; page-break-before: always; page-break-inside: auto; }
  .sec:first-of-type { page-break-before: avoid; }
  .sec-hdr { display: flex; flex-wrap: wrap; align-items: center; gap: 14px; padding: 7px 10px; margin-bottom: 4px; background: #ede7f6; border-left: 6px solid #4a148c; border-radius: 3px; }
  .sec-hdr .sec-corso { font-size: 14px; font-weight: 800; color: #4a148c; }
  .sec-hdr .sec-giorno { font-size: 13px; font-weight: 700; color: #0d47a1; }
  .sec-hdr .sec-gruppo { font-size: 13px; font-weight: 700; color: #ef6c00; }
  .sec-hdr .sec-count { margin-left: auto; font-size: 11px; font-weight: 700; color: #1b5e20; background: #e8f5e9; padding: 2px 8px; border-radius: 3px; }
  .day-sub { margin: 8px 0 3px; font-size: 12px; font-weight: 700; color: #0d47a1; border-bottom: 1px solid #90caf9; padding-bottom: 2px; }
  .tbl { width: 100%; border-collapse: collapse; font-size: 11px; }
  .tbl thead tr { background: #eceff1; color: #37474f; }
  .tbl th, .tbl td { border: 1px solid #cfd8dc; padding: 3px 6px; text-align: left; }
  .tbl th { font-size: 10px; letter-spacing: 0.3px; }
  .tbl td.strong { font-weight: 700; }
  .tbl td.mono { font-family: Consolas, "Courier New", monospace; color: #555; }
  .tbl td.nowrap, .tbl th.nowrap { white-space: nowrap; }
  .tbl td.num { text-align: right; width: 28px; color: #666; }
  .tbl td.stato { font-size: 10px; white-space: nowrap; }
  .tbl td .cod-inline { display: block; font-size: 9px; color: #888; font-weight: 400; }
  .tbl td .giorni-inline { display: block; font-size: 9px; color: #0d47a1; font-weight: 600; }
  .tbl td.price { text-align: right; font-family: Consolas, monospace; font-weight: 700; white-space: nowrap; }
  .tbl tbody tr:nth-child(even) { background: #fafafa; }
  .tbl tfoot .subtot td { background: #f3e5f5; font-weight: 700; color: #4a148c; font-size: 10px; }
  .grand { margin-top: 16px; padding: 10px 14px; background: #1b5e20; color: white; font-size: 14px; font-weight: 800; display: flex; justify-content: space-between; border-radius: 3px; }
  .grand .lbl { letter-spacing: 0.5px; }
  .grand .amt { font-family: Consolas, monospace; font-size: 16px; }
  .ft { margin-top: 12px; font-size: 9px; color: #888; border-top: 1px dashed #ccc; padding-top: 4px; text-align: right; }
</style>
</head><body>
  <div class="doc-hdr">
    <h1>🧾 LISTA ADDEBITI · Corsi Extra</h1>
    <div class="sub">canossaHUB · A.S. ${esc(annoLabel)} · tabulato per Corso · Giorno · Gruppo (nuova pagina ad ogni sezione)</div>
    <div class="stamp">🕐 Stampato il ${esc(oraStampaStr)}${batchId ? ` · batch <span style="font-family:Consolas,monospace">${esc(batchId)}</span>` : ""}</div>
    ${filtriHtml}
  </div>
  ${tabellaCrono}
  <div class="grand">
    <span class="lbl">TOTALE COMPLESSIVO (${nSezioni} sezioni · ${totaleRighe} righe)</span>
    <span class="amt">€ ${esc(fmtImporto(totaleImporto))}</span>
  </div>
  <div class="ft">Righe raggruppate per Corso · Giorno · Gruppo. Le iscrizioni con più giorni compaiono in ogni giorno (righe totali ≥ n° iscrizioni). Stampato il ${esc(oraStampaStr)}</div>
<script>window.onload = function(){ setTimeout(function(){ window.print(); }, 250); };<\/script>
</body></html>`;
    const w = window.open("", "_blank");
    if (!w) { alert("Popup bloccato dal browser. Abilita i popup per stampare."); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();

    // Se abbiamo marcato, ricarica la lista per riflettere il nuovo stato
    if (marcate) {
      try { await load(); } catch { /* ignore */ }
    }
    void batchAt; // batchAt usato come metadata, non renderizzato direttamente
  };

  return (
    <div data-testid="extra-crono-view" style={{ padding: "12px 20px" }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-end", marginBottom: 10, padding: 12, background: "#fafafa", border: "1px solid #e0e0e0" }}>
        <Field label="ANNO ISCRIZIONE">
          <select data-testid="crono-anno" value={filterAnno} onChange={e => setFilterAnno(e.target.value)}
            style={{ padding: "6px 10px", border: "1px solid #ccc", fontSize: 12 }}>
            <option value="">Tutti</option>
            {anniDisponibili.map(a => <option key={a} value={a}>{fmtAnnoScol(a)}</option>)}
          </select>
        </Field>
        <Field label="RICERCA">
          <input data-testid="crono-search" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Cognome, nome, codice, corso, classe…"
            style={{ width: 240, padding: "6px 10px", border: "1px solid #ccc", fontSize: 12 }} />
        </Field>
        <Field label="CORSO">
          <select data-testid="crono-corso" value={filterCorso} onChange={e => setFilterCorso(e.target.value)}
            style={{ padding: "6px 10px", border: "1px solid #ccc", fontSize: 12, maxWidth: 260 }}>
            <option value="">Tutti i corsi</option>
            {corsiDisponibili.map(([code, descr]) => (
              <option key={code} value={code}>{code} — {descr}</option>
            ))}
          </select>
        </Field>
        <Field label="GIORNO">
          <select data-testid="crono-giorno" value={filterGiorno} onChange={e => setFilterGiorno(e.target.value)}
            style={{ padding: "6px 10px", border: "1px solid #ccc", fontSize: 12 }}>
            <option value="">Tutti i giorni</option>
            {giorniDisponibili.map(g => (
              <option key={g} value={String(g)}>{DAYS_LABELS[g]}</option>
            ))}
          </select>
        </Field>
        <Field label="GRUPPO">
          <select data-testid="crono-gruppo" value={filterGruppo} onChange={e => setFilterGruppo(e.target.value)}
            title="Gruppo doposcuola primaria: 1 = classi 1-2, 2 = classi 3-4-5"
            style={{ padding: "6px 10px", border: "1px solid #ccc", fontSize: 12 }}>
            <option value="">Tutti</option>
            <option value="1">Gruppo 1 (cl.1-2)</option>
            <option value="2">Gruppo 2 (cl.3-4-5)</option>
          </select>
        </Field>
        <Field label="ORDINE">
          <select data-testid="crono-sort" value={sortDir} onChange={e => setSortDir(e.target.value)}
            style={{ padding: "6px 10px", border: "1px solid #ccc", fontSize: 12 }}>
            <option value="desc">Più recenti prima</option>
            <option value="asc">Più vecchie prima</option>
          </select>
        </Field>
        <Field label="STATO">
          <select data-testid="crono-stato" value={filterStato} onChange={e => setFilterStato(e.target.value)}
            style={{ padding: "6px 10px", border: "1px solid #ccc", fontSize: 12, minWidth: 160 }}>
            <option value="">Tutti</option>
            <option value="pending">● In attesa</option>
            <option value="in_attesa_pagamento">💳 Inviato link pagamento</option>
            <option value="confermata_genitore">✓✓ Confermata genitore</option>
            <option value="pagata_da_verificare">💳 In attesa pagamento</option>
            <option value="pagata">💳 Confermata pagata</option>
            <option value="waiting">⏳ In lista d&apos;attesa</option>
            <option value="rejected">✗ Rifiutata</option>
            <option value="variazione">🔄 Variazione</option>
          </select>
        </Field>
        {someFilterOn && (
          <button data-testid="crono-clear" onClick={resetFiltri}
            style={{ padding: "6px 10px", background: "white", color: "#c62828", border: "1px solid #c62828", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
            ✕ Pulisci
          </button>
        )}
        <button data-testid="crono-refresh" onClick={load}
          style={{ padding: "6px 14px", background: "#1565c0", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
          🔄 Aggiorna
        </button>
        <button data-testid="crono-stampa-addebiti" onClick={stampaListaAddebiti}
          title="Apre una nuova finestra con la LISTA ADDEBITI: tutte le iscrizioni (filtri attivi) raggruppate per Corso · Giorno · Gruppo, una sezione per pagina. Colonne: N°, POS., Anno Iscr., Data/Ora, Alunno, Settore, Classe, Gruppo, Corso·Giorni, Prezzo, Stato. Le iscrizioni con più giorni compaiono in ogni giorno."
          style={{ padding: "6px 14px", background: "#6a1b9a", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
          🧾 Lista Addebiti (per corso/giorno/gruppo)
        </button>
        <button data-testid="crono-ricalcola-prezzi"
          onClick={async () => {
            const annoLbl = filterAnno ? fmtAnnoScol(filterAnno) : "TUTTI GLI ANNI";
            const scope = filterAnno ? `solo anno ${annoLbl}` : "tutte le iscrizioni di tutti gli anni";
            if (!window.confirm(
              `🔄 Ricalcola prezzi da listino (${scope}).\n\n` +
              `Aggiorna il prezzo delle iscrizioni con prezzo 0/mancante usando i campi 'tariffe' e 'prezzi' configurati sui corsi.\n\n` +
              `Le iscrizioni con prezzo già impostato (>0) non verranno modificate.\n\nContinuare?`
            )) return;
            try {
              const params = new URLSearchParams({ only_zero: "true" });
              if (filterAnno) params.set("anno", String(filterAnno));
              const r = await api.post(`/mensa-matrice/extra-iscrizioni/_admin/ricalcola-prezzi?${params.toString()}`);
              const d = r.data || {};
              alert(
                `✓ Ricalcolo completato.\n\n` +
                `• Iscrizioni analizzate: ${d.matched}\n` +
                `• Aggiornate: ${d.updated}\n` +
                `• Già corrette (skip): ${d.skipped}\n` +
                `• Senza prezzo a listino: ${d.mancanti_listino}` +
                (d.esempi_corsi_senza_listino?.length ? `\n\nCorsi senza listino: ${d.esempi_corsi_senza_listino.map(x => `${x.corso}/${x.anno}`).join(", ")}` : "")
              );
              await load();
            } catch (e) {
              alert(`Errore: ${e.response?.data?.detail || e.message}`);
            }
          }}
          title="Ricalcola il prezzo delle iscrizioni con prezzo 0/mancante leggendo le tariffe/prezzi configurati sui corsi. Non sovrascrive prezzi >0 già impostati. Filtra per anno se l'anno è selezionato."
          style={{ padding: "6px 14px", background: "#00838f", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
          🔄 Ricalcola prezzi
        </button>
        {filterAnno && (
          <details data-testid="crono-danger-menu" style={{ position: "relative" }}>
            <summary style={{ listStyle: "none", cursor: "pointer", padding: "6px 12px", background: "#eeeeee", border: "1px solid #bdbdbd", color: "#616161", fontSize: 12, fontWeight: 700, borderRadius: 3, display: "inline-flex", alignItems: "center", gap: 4, userSelect: "none" }}
              title="Menu azioni bulk (attenzione: azioni distruttive)">
              ⋯
            </summary>
            <div style={{ position: "absolute", top: "calc(100% + 4px)", right: 0, background: "white", border: "2px solid #c62828", borderRadius: 4, padding: 12, minWidth: 280, boxShadow: "0 4px 16px rgba(0,0,0,0.25)", zIndex: 500 }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: "#c62828", letterSpacing: 0.5, marginBottom: 8, textTransform: "uppercase" }}>⚠ Zona pericolosa</div>
              <button data-testid="crono-chiudi-anno"
                onClick={async () => {
                  const annoNum = parseInt(filterAnno, 10);
                  if (!annoNum) return;
                  const labelAnno = `${annoNum}/${String(annoNum + 1).slice(-2)}`;
                  const conferma1 = window.confirm(
                    `⚠️ Marca TUTTE le iscrizioni anno ${labelAnno} con stato '🔒 CHIUSA'.\n\nL'operazione sovrascrive lo stato corrente di TUTTE le iscrizioni di quell'anno (anche già pagate, rifiutate, ecc).\n\nContinuare?`
                  );
                  if (!conferma1) return;
                  const conferma2 = window.prompt(
                    `Per confermare digita l'anno scolastico nel formato '${labelAnno}':`
                  );
                  if (conferma2 !== labelAnno) {
                    alert(`Annullato: hai digitato '${conferma2}' invece di '${labelAnno}'.`);
                    return;
                  }
                  try {
                    const r = await api.post(`/mensa-matrice/extra-iscrizioni/_admin/chiudi-anno/${annoNum}`);
                    alert(`✓ OK. Iscrizioni aggiornate: ${r.data.modified} / ${r.data.matched}.`);
                    await load();
                  } catch (e) {
                    alert(`Errore: ${e.response?.data?.detail || e.message}`);
                  }
                }}
                title={`Imposta stato 'chiusa' per tutte le iscrizioni dell'anno ${filterAnno}/${String(parseInt(filterAnno, 10) + 1).slice(-2)}`}
                style={{ width: "100%", padding: "8px 12px", background: "#37474f", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, borderRadius: 3, textAlign: "left" }}>
                🔒 Chiudi tutte ({filterAnno}/{String(parseInt(filterAnno, 10) + 1).slice(-2)})
              </button>
              <div style={{ fontSize: 10, color: "#888", marginTop: 6, lineHeight: 1.3 }}>
                Richiede doppia conferma (dialog + digitazione anno esatto).
              </div>
            </div>
          </details>
        )}
        {onNewIscrizione && (
          <button data-testid="crono-new-iscrizione" onClick={onNewIscrizione}
            style={{ padding: "6px 14px", background: "#ef6c00", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span style={{ fontSize: 14, fontWeight: 900, lineHeight: 1 }}>+</span> Nuova iscrizione
          </button>
        )}
        <div style={{ marginLeft: "auto", fontSize: 12, color: "#333" }} data-testid="crono-stats">
          <strong>{filtered.length}</strong> iscrizioni · <strong>€ {fmtImporto(totalePrezzo)}</strong>
        </div>
      </div>

      {err && <div style={{ padding: 8, background: "#ffebee", color: "#c62828", border: "1px solid #ef9a9a", marginBottom: 8, fontSize: 12 }}>{err}</div>}
      {loading && <div style={{ padding: 12, fontSize: 12, color: "#666" }}>Caricamento…</div>}

      <style>{`@keyframes pulse-overdue { 0%, 100% { opacity: 1 } 50% { opacity: 0.55 } }`}</style>

      <div style={{ overflowX: "auto", border: "1px solid #ddd" }}>
        <table data-testid="crono-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ background: "#37474f", color: "white" }}>
              <th style={{ ...th, textAlign: "center" }} title="Numero progressivo della riga nell'elenco filtrato">N°</th>
              <th style={{ ...th, textAlign: "center" }} title="Posizione nell'ordine di iscrizione del corso (1 = primo iscritto)">POS.</th>
              <th style={th}>ANNO ISCR.</th>
              <th style={th}>DATA / ORA</th>
              <th style={th}>ALUNNO</th>
              <th style={th}>SETTORE</th>
              <th style={th}>CLASSE</th>
              <th style={{ ...th, textAlign: "center" }} title="Gruppo doposcuola primaria: 1 = classi 1-2, 2 = classi 3-4-5">GRUPPO</th>
              <th style={th}>CORSO · GIORNI</th>
              <th style={{ ...th, textAlign: "right", minWidth: 110 }}>PREZZO €</th>
              <th style={{ ...th, textAlign: "center", minWidth: 160 }}>STATO</th>
              <th style={{ ...th, textAlign: "center", minWidth: 140 }}>CONTABILE</th>
              <th style={{ ...th, textAlign: "center", minWidth: 110 }}>MOV BANCA</th>
              <th style={{ ...th, textAlign: "center", minWidth: 320 }} title="Azioni rapide sull'iscrizione">AZIONI</th>
            </tr>
          </thead>
          <tbody>
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={14} style={{ padding: 20, textAlign: "center", color: "#999" }}>Nessuna iscrizione</td></tr>
            )}
            {filtered.map((it, idx) => {
              const posInfo = it._id ? positionByItemId.get(it._id) : null;
              const giorniSet = giorniToSet(it.giorni);
              const giorniLabel = [...giorniSet].sort((a, b) => a - b).map(g => DAYS_LABELS[g]?.slice(0, 3)).join(", ") || "—";
              // 🆕 Avviso 24h: iscrizione in attesa pagamento da > 24h ma senza contabile caricata
              const sCur = statoOf(it);
              // 🆕 Badge colonna ALUNNO: recesso = ha una data_recesso (a prescindere dallo stato,
              //   es. "chiusa"/"pagata"); variata = variazione/cambio corso ma NON receduta.
              const hasRecesso = !!String(it.data_recesso || "").trim();
              const isVariata = !!it.variata && !hasRecesso;
              const isAttesaPag = (sCur === "in_attesa_pagamento" || sCur === "accettata_mail_inviata" || sCur === "confermata_genitore");
              let oreDaLink = null;
              if (isAttesaPag && !it.has_contabile && it.mail_conferma_inviata_at) {
                try {
                  const dt = new Date(it.mail_conferma_inviata_at).getTime();
                  oreDaLink = (Date.now() - dt) / (1000 * 60 * 60);
                } catch { /* ignore */ }
              }
              const overdue24h = oreDaLink !== null && oreDaLink >= 24;
              const rowBg = overdue24h
                ? "#fff3e0"  // arancione tenue per overdue
                : (idx % 2 ? "#fafafa" : "white");
              const rowBorder = overdue24h
                ? "1px solid #ef6c00"
                : "1px solid #eee";
              return (
                <tr key={it._id || idx} data-testid={`crono-row-${idx}`}
                  style={{ borderBottom: rowBorder, background: rowBg,
                    boxShadow: overdue24h ? "inset 4px 0 0 0 #ef6c00" : undefined,
                  }}>
                  <td data-testid={`crono-rownum-${idx}`} style={{ ...td, textAlign: "center", fontWeight: 700, fontFamily: "Consolas, monospace", color: "#37474f", fontSize: 12, background: idx % 2 ? "#f5f5f5" : "#fafafa" }}>
                    {idx + 1}
                  </td>
                  <td data-testid={`crono-pos-cell-${idx}`} style={{ ...td, textAlign: "center", fontWeight: 700, fontFamily: "Consolas, monospace", padding: "4px 6px" }}>
                    {editingPosId === it._id ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 4, justifyContent: "center" }}>
                        <input
                          data-testid={`crono-pos-input-${idx}`}
                          autoFocus
                          type="number"
                          min="1"
                          value={editingPosVal}
                          onChange={e => setEditingPosVal(e.target.value)}
                          onKeyDown={e => { if (e.key === "Enter") salvaPosOverride(it); if (e.key === "Escape") chiudiPosEdit(); }}
                          placeholder="auto"
                          disabled={savingPos}
                          style={{ width: 56, padding: "3px 4px", border: "1px solid #1565c0", fontSize: 12, fontFamily: "Consolas, monospace", textAlign: "center", borderRadius: 3 }}
                        />
                        <button data-testid={`crono-pos-save-${idx}`} onClick={() => salvaPosOverride(it)} disabled={savingPos}
                          title="Salva (Invio). Lascia vuoto per tornare alla POS automatica."
                          style={{ padding: "2px 6px", background: "#1b5e20", color: "white", border: "none", fontSize: 10, fontWeight: 800, cursor: savingPos ? "wait" : "pointer", borderRadius: 2 }}>✓</button>
                        <button data-testid={`crono-pos-cancel-${idx}`} onClick={chiudiPosEdit} disabled={savingPos}
                          title="Annulla (Esc)"
                          style={{ padding: "2px 6px", background: "#c62828", color: "white", border: "none", fontSize: 10, fontWeight: 800, cursor: savingPos ? "wait" : "pointer", borderRadius: 2 }}>✕</button>
                      </div>
                    ) : posInfo ? (
                      <div data-testid={`crono-pos-${idx}`}
                        onClick={() => apriPosEdit(it, posInfo.pos)}
                        title={posInfo.isOverride
                          ? `POS impostata manualmente a ${posInfo.pos} (automatica era ${posInfo.auto}/${posInfo.total}). Click per modificare.`
                          : `Posizione automatica ${posInfo.pos} su ${posInfo.total} iscritti. Click per modificare manualmente.`}
                        style={{ cursor: "pointer", padding: "2px 4px", borderRadius: 3, transition: "background 0.15s" }}
                        onMouseEnter={e => e.currentTarget.style.background = "#e3f2fd"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                        <span style={{
                          color: posInfo.isOverride ? "#6a1b9a" : (posInfo.pos === 1 ? "#1b5e20" : "#0d47a1"),
                          fontSize: 13,
                          textDecoration: posInfo.isOverride ? "underline" : "none",
                        }}>
                          {posInfo.isOverride && <span style={{ fontSize: 9, marginRight: 2 }}>✎</span>}
                          {posInfo.pos}
                        </span>
                        <span style={{ color: "#888", fontSize: 10 }}> / {posInfo.total}</span>
                      </div>
                    ) : "—"}
                  </td>
                  <td style={td}>{fmtAnnoScol(it.anno)}</td>
                  <td style={{ ...td, fontFamily: "Consolas, monospace", whiteSpace: "nowrap" }}>{fmtDateTime(it.created_at)}</td>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{(it.cognome || "") + " " + (it.nome || "")}</div>
                    {(isVariata || hasRecesso) && (
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", margin: "3px 0" }}>
                        {isVariata && (
                          <span data-testid={`crono-badge-variata-${it._id}`}
                            title={`Iscrizione VARIATA${it.data_cambio ? " il " + it.data_cambio : ""}${it.giorni_originali ? ` · giorni ${[...giorniToSet(it.giorni_originali)].sort((a, b) => a - b).map(g => DAYS_LABELS[g]?.slice(0, 3)).join(", ") || "—"} → ${giorniLabel}` : ""}`}
                            style={{ background: "#e65100", color: "white", fontSize: 9, fontWeight: 800, letterSpacing: 0.5, padding: "2px 7px", borderRadius: 3, whiteSpace: "nowrap", boxShadow: "0 1px 2px rgba(0,0,0,0.2)" }}>
                            🔄 VARIATA
                          </span>
                        )}
                        {hasRecesso && (
                          <span data-testid={`crono-badge-recesso-${it._id}`}
                            title={`Recesso dal ${it.data_recesso}`}
                            style={{ background: "#d32f2f", color: "white", fontSize: 9, fontWeight: 800, letterSpacing: 0.5, padding: "2px 7px", borderRadius: 3, whiteSpace: "nowrap", boxShadow: "0 1px 2px rgba(0,0,0,0.2)" }}>
                            ⊘ RECESSO
                          </span>
                        )}
                      </div>
                    )}
                    <div style={{ fontSize: 10, color: "#888" }}>Cod. {it.alunno_cod}</div>
                  </td>
                  <td style={td}>{settoreLabel(it.settore)}</td>
                  <td style={td}>{it.classe || "—"}</td>
                  <td style={{ ...td, textAlign: "center", fontFamily: "Consolas, monospace", fontWeight: 700 }}>
                    {(() => {
                      const g = classToGruppo(it.classe);
                      if (g === 1) return <span title="Classi 1-2 primaria" style={{ padding: "2px 8px", background: "#1565c0", color: "white", borderRadius: 2, fontSize: 10 }}>1</span>;
                      if (g === 2) return <span title="Classi 3-4-5 primaria" style={{ padding: "2px 8px", background: "#6a1b9a", color: "white", borderRadius: 2, fontSize: 10 }}>2</span>;
                      return <span style={{ color: "#bbb" }}>—</span>;
                    })()}
                  </td>
                  <td style={{ ...td, minWidth: 240 }}>
                    <button onClick={onOpenCorso ? () => onOpenCorso(it.corso_codice, it.anno) : undefined}
                      data-testid={`crono-btn-corso-${idx}`}
                      title={`Apre "Corsi ›› Alunni" filtrato sul corso "${it.corso_descrizione || it.corso_codice}" per verificare disponibilità posti prima di inviare la mail di conferma`}
                      style={{
                        padding: "6px 10px",
                        background: onOpenCorso ? "#0d47a1" : "#b0bec5",
                        color: "white",
                        border: "2px solid #0d47a1",
                        fontWeight: 800,
                        fontSize: 11,
                        borderRadius: 3,
                        cursor: onOpenCorso ? "pointer" : "not-allowed",
                        boxShadow: onOpenCorso ? "0 1px 3px rgba(13,71,161,0.4)" : "none",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "flex-start",
                        width: "100%",
                        height: 60,
                        textAlign: "left",
                        lineHeight: 1.25,
                        whiteSpace: "normal",
                        overflow: "hidden",
                      }}>
                      <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 2 }}>
                        📋 {it.corso_descrizione || it.attivita || it.corso_codice}
                      </div>
                      <div style={{ fontSize: 10, opacity: 0.95, fontWeight: 600 }}>
                        📅 {giorniLabel || "—"}
                      </div>
                      <div style={{ fontSize: 9, opacity: 0.85, fontWeight: 600 }}>
                        Verifica disponibilità ›
                      </div>
                    </button>
                    <div style={{ marginTop: 4 }}>
                      {it.usa_mensa === false ? (
                        <button data-testid={`crono-no-mensa-${idx}`}
                          onClick={async () => { if (window.confirm(`Ripristinare la MENSA per ${it.cognome || ""} ${it.nome || ""}?\nTornerà a occupare la matrice nei giorni del corso.`)) { await api.put(`/mensa-matrice/extra-iscrizioni/${it._id}`, { usa_mensa: true }); await load(); } }}
                          title="Alunno segnato come 'non mangia in mensa' — clicca per RIPRISTINARE la mensa (tornerà a occupare la matrice)"
                          style={{ cursor: "pointer", border: "none", display: "inline-block", padding: "2px 7px",
                                   background: "#c62828", color: "white", fontSize: 10, fontWeight: 800,
                                   borderRadius: 2, letterSpacing: 0.3 }}>
                          🚫 NO MENSA · ripristina
                        </button>
                      ) : (
                        <button data-testid={`crono-toggle-mensa-${idx}`}
                          onClick={async () => { if (window.confirm(`Segnare che ${it.cognome || ""} ${it.nome || ""} NON mangia in mensa?\nNon occuperà più la matrice mensa nei giorni del corso (resta comunque iscritto).`)) { await api.put(`/mensa-matrice/extra-iscrizioni/${it._id}`, { usa_mensa: false }); await load(); } }}
                          title="Segna che l'alunno NON mangia in mensa: non occuperà la matrice/refettori nei giorni del corso, pur restando iscritto"
                          style={{ cursor: "pointer", border: "1px solid #cfd8dc", background: "white",
                                   color: "#90a4ae", fontSize: 9, fontWeight: 700, borderRadius: 2, padding: "1px 6px" }}>
                          🚫 segna &quot;no mensa&quot;
                        </button>
                      )}
                    </div>
                    <VariazioneBadges it={it} />
                  </td>
                  {/* 🆕 Uniformato a "Corsi ›› Alunni":
                      PREZZO € = valore listino + DROPDOWN STATO sotto
                      STATO   = bottone CONTABILE (verde se pagata) + bottone MOV sotto
                      AZIONI  = keyboard 13 bottoni con sigle (M, M+, LA, SO, P, UP, VD, VP, SC, ST, R, E, X) */}
                  {(() => {
                    const stato = statoOf(it);
                    const map = {
                      pending:                { bg: "#9e9e9e", color: "white", txt: "● INSERITA" },
                      waiting:                { bg: "#ef6c00", color: "white", txt: "⏳ LISTA D'ATTESA" },
                      in_attesa_pagamento:    { bg: "#ba68c8", color: "white", txt: "💳 INVIATO LINK PAG." },
                      accettata_mail_inviata: { bg: "#ba68c8", color: "white", txt: "💳 INVIATO LINK PAG." },
                      confirmed:              { bg: "#ba68c8", color: "white", txt: "💳 INVIATO LINK PAG." },
                      confermata_genitore:    { bg: "#ba68c8", color: "white", txt: "💳 INVIATO LINK PAG." },
                      pagata_da_verificare:   { bg: "#03a9f4", color: "white", txt: "🔍 DA VERIFICARE" },
                      pagata:                 { bg: "#2e7d32", color: "white", txt: "✓ PAGATA" },
                      scaduta:                { bg: "#212121", color: "white", txt: "⏰ SCADUTA" },
                      recesso:                { bg: "#d32f2f", color: "white", txt: "⊘ RECESSO" },
                      rejected:               { bg: "#ba68c8", color: "white", txt: "💳 INVIATO LINK PAG." },
                      chiusa:                 { bg: "#ffffff", color: "#37474f", txt: "🔒 CHIUSA", border: "1px solid #37474f" },
                    };
                    const cfg = map[stato] || map.pending;
                    const cambiaStato = async (nuovo) => {
                      if (nuovo === stato) return;
                      if (!window.confirm(`Cambiare lo stato di ${it.cognome} ${it.nome} a "${(map[nuovo] || {}).txt || nuovo}"?`)) return;
                      try {
                        if (nuovo === "recesso") {
                          const today = new Date().toISOString().slice(0, 10);
                          await api.put(`/mensa-matrice/extra-iscrizioni/${it._id}`, { data_recesso: today });
                        } else {
                          if (stato === "recesso") {
                            await api.put(`/mensa-matrice/extra-iscrizioni/${it._id}`, { data_recesso: "" });
                          }
                          await api.put(`/mensa-matrice/extra-iscrizioni/${it._id}`, { stato_iscrizione: nuovo });
                        }
                        await load();
                      } catch (e) { setErr(e.response?.data?.detail || "Errore cambio stato"); }
                    };
                    // Prezzo
                    const pz = it.prezzo;
                    let prezzoLabel = "—";
                    let prezzoColor = "#bbb";
                    if (pz != null && pz !== "" && Number(pz) !== 0) {
                      const n = Number(pz);
                      prezzoLabel = Number.isFinite(n) ? n.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : String(pz);
                      prezzoColor = "#0d47a1";
                    }
                    // Movimenti
                    const mc = movimentiCounts[it._id];
                    const total = mc && typeof mc === "object" ? mc.total : (typeof mc === "number" ? mc : 0);
                    const paid = mc && typeof mc === "object" ? mc.paid : 0;
                    let movBg = "#9e9e9e";
                    let movTip = "Nessun movimento contabile collegato (verifica causale corso)";
                    if (total > 0 && paid === 0) { movBg = "#c62828"; movTip = `⚠ ${total} movimento/i collegato/i · NESSUNO ancora pagato`; }
                    else if (total > 0 && paid > 0) { movBg = "#2e7d32"; movTip = `${total} movimento/i collegato/i · ${paid} PAGATO/I`; }
                    const isPagata = stato === "pagata";
                    const contabileBg = isPagata ? "#2e7d32" : "#03a9f4";
                    const isRecesso = stato === "recesso";
                    const isFinal = (stato === "pagata" || stato === "recesso");
                    const canScaduta = stato === "in_attesa_pagamento" || stato === "accettata_mail_inviata";
                    const isPaid = isPagata;
                    const clr = (defaultColor) => isPaid ? "#2e7d32" : defaultColor;
                    const btnStyle = (bg, v = true) => ({
                      background: v ? bg : "#ffffff",
                      color: v ? "white" : "#b0bec5",
                      border: v ? "2px solid " + bg : "2px solid #cfd8dc",
                      padding: 0,
                      fontSize: 11, fontWeight: 700,
                      cursor: v ? "pointer" : "default",
                      borderRadius: 3,
                      pointerEvents: v ? "auto" : "none",
                      width: 30, height: 24,
                      boxSizing: "border-box", lineHeight: 1,
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      flex: "0 0 30px",
                    });
                    return (
                      <>
                        {/* PREZZO € */}
                        <td style={{ ...td, textAlign: "right", fontFamily: "Consolas, monospace", fontWeight: 700, color: prezzoColor, width: 120, minWidth: 120 }}>
                          {/* 🆕 Feb 2026: badge SO inviato spostato PRIMA del prezzo */}
                          {it.mail_sollecito_inviato_at && (
                            <div data-testid={`crono-sollecito-info-${idx}`}
                              title={`Sollecito inviato da ${it.mail_sollecito_inviato_by || "?"} a ${it.mail_sollecito_inviato_to || ""}`}
                              style={{
                                marginBottom: 4, fontSize: 9, color: "#ad1457",
                                fontWeight: 700, letterSpacing: 0.2,
                                background: "#fce4ec", border: "1px solid #f8bbd0",
                                padding: "1px 4px", borderRadius: 3,
                                display: "block", textAlign: "center",
                              }}>
                              📣 SO: {fmtDateTime(it.mail_sollecito_inviato_at)}
                            </div>
                          )}
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", padding: "4px 6px", width: "100%", height: 60, color: prezzoColor, fontSize: 14, fontWeight: 800, borderRadius: 3, boxSizing: "border-box", lineHeight: 1.2, border: "1px solid #e0e0e0", background: "#fafafa" }}>
                            {prezzoLabel}
                            {it.prezzo_auto && prezzoLabel !== "—" && (
                              <span title="Prezzo calcolato al volo dal listino corso (non persistito)."
                                style={{ marginLeft: 4, color: "#1565c0", cursor: "help", display: "inline-flex", alignItems: "center" }}>
                                <RefreshCw size={13} strokeWidth={2.5} />
                              </span>
                            )}
                          </div>
                        </td>
                        {/* STATO */}
                        <td style={{ ...td, textAlign: "center", width: 160, minWidth: 160 }}>
                          <select value={stato} onChange={(e) => cambiaStato(e.target.value)}
                            data-testid={`crono-stato-select-${idx}`}
                            title={`Stato: ${cfg.txt} — clicca per modificare`}
                            style={{ display: "block", fontSize: 11, padding: "6px 8px", width: "100%", height: 60, border: cfg.border || `2px solid ${cfg.bg}`, background: cfg.bg, color: cfg.color, cursor: "pointer", fontWeight: 800, borderRadius: 3, textAlignLast: "center", boxSizing: "border-box", lineHeight: 1.2, whiteSpace: "normal", textOverflow: "ellipsis" }}>
                            <option value="pending" style={{ background: "white", color: "#333" }}>● INSERITA</option>
                            <option value="waiting" style={{ background: "white", color: "#333" }}>⏳ LISTA D&apos;ATTESA</option>
                            <option value="in_attesa_pagamento" style={{ background: "white", color: "#333" }}>💳 INVIATO LINK PAG.</option>
                            <option value="pagata_da_verificare" style={{ background: "white", color: "#333" }}>🔍 DA VERIFICARE</option>
                            <option value="pagata" style={{ background: "white", color: "#333" }}>✓ PAGATA</option>
                            <option value="scaduta" style={{ background: "white", color: "#333" }}>⏰ SCADUTA</option>
                            <option value="recesso" style={{ background: "white", color: "#333" }}>⊘ RECESSO</option>
                            <option value="chiusa" style={{ background: "white", color: "#333" }}>🔒 CHIUSA</option>
                          </select>
                        </td>
                        {/* CONTABILE */}
                        <td style={{ ...td, textAlign: "center", width: 140, minWidth: 140 }}>
                          {it.has_contabile ? (
                            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/mensa-matrice/extra-iscrizioni/${it._id}/contabile?inline=1`}
                              target="_blank" rel="noopener noreferrer"
                              data-testid={`crono-contabile-${idx}`}
                              title={`Visualizza contabile: ${it.contabile_filename || ""}${it.contabile_uploaded_at ? "\nCaricata il " + fmtDateTime(it.contabile_uploaded_at) : ""}${isPagata ? "\n✓ PAGATA" : ""}`}
                              style={{ display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, padding: "4px 6px", width: "100%", height: 60, border: `2px solid ${contabileBg}`, background: contabileBg, color: "white", cursor: "pointer", fontWeight: 800, borderRadius: 3, textDecoration: "none", textAlign: "center", boxSizing: "border-box", lineHeight: 1.2, whiteSpace: "normal" }}>
                              📎 Contabile{it.contabile_uploaded_at ? ` ${new Date(it.contabile_uploaded_at).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit" })}` : ""}
                            </a>
                          ) : (
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, padding: "4px 6px", width: "100%", height: 60, border: "2px dashed #bdbdbd", background: "#fafafa", color: "#999", fontWeight: 600, borderRadius: 3, textAlign: "center", boxSizing: "border-box", lineHeight: 1.2 }}>
                              no contab.
                            </div>
                          )}
                          {it.contabilizzata === true && (
                            <div data-testid={`crono-contab-${idx}`}
                              title={`Già contabilizzata il ${it.contabilizzata_at ? fmtDateTime(it.contabilizzata_at) : "—"}`}
                              style={{ marginTop: 3, fontSize: 9, color: "#1b5e20", fontWeight: 800, letterSpacing: 0.3 }}>
                              ✓ CONTAB.
                            </div>
                          )}
                        </td>
                        {/* MOV BANCA */}
                        <td style={{ ...td, textAlign: "center", width: 110, minWidth: 110 }}>
                          {mc !== undefined ? (
                            <button data-testid={`crono-mov-${idx}`}
                              onClick={() => apriMovimenti(it)}
                              title={movTip}
                              style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "4px 6px", background: movBg, color: "white", border: `2px solid ${movBg}`, fontSize: 12, fontWeight: 800, cursor: "pointer", borderRadius: 3, width: "100%", height: 60, letterSpacing: 0.3, lineHeight: 1.2, boxSizing: "border-box", whiteSpace: "normal" }}>
                              {total} mov{total > 0 ? ` · ${paid} ✓` : ""}
                            </button>
                          ) : (
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "4px 6px", width: "100%", height: 60, color: "#bbb", border: "2px dashed #eee", borderRadius: 3, boxSizing: "border-box", fontSize: 10 }}>—</div>
                          )}
                        </td>
                        {/* AZIONI */}
                        <td style={{ ...td, textAlign: "center", whiteSpace: "nowrap" }}>
                          <span style={{ display: "inline-flex", gap: 4, flexWrap: "nowrap", justifyContent: "center" }}>
                            <button onClick={() => apriMailTemplate(it, "sollecito_pagamento")} data-testid={`crono-btn-SO-${idx}`}
                              title={it.mail_sollecito_inviato_at ? `Sollecito già inviato il ${fmtDateTime(it.mail_sollecito_inviato_at)} — reinvia` : "Invia sollecito di pagamento"}
                              style={{
                                // 🆕 Feb 2026: SO sempre attivo se stato ≠ "pagata" e ≠ "recesso"
                                ...btnStyle(clr("#ad1457"), !isPagata && !isRecesso),
                                border: it.mail_sollecito_inviato_at ? "2px solid #ad1457" : undefined,
                                background: it.mail_sollecito_inviato_at ? "white" : undefined,
                                color: it.mail_sollecito_inviato_at ? "#ad1457" : undefined,
                              }}>SO</button>
                            <button onClick={() => pagatoConOSenzaMail(it)} data-testid={`crono-btn-P-${idx}`}
                              title="Marca PAGATA e CONFERMATA (senza mail) — richiede contabile"
                              style={btnStyle(clr("#1b5e20"), it.has_contabile && stato !== "pagata")}>P</button>
                            <button onClick={() => uploadContabileAdmin(it)} data-testid={`crono-btn-UP-${idx}`}
                              title={it.has_contabile ? "RICARICA contabile (sostituisce)" : "Carica contabile bonifico"}
                              style={btnStyle(clr(it.has_contabile ? "#00838f" : "#0277bd"), !isFinal && stato !== "scaduta")}>UP</button>
                            <button onClick={it.has_contabile ? () => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/mensa-matrice/extra-iscrizioni/${it._id}/contabile?inline=1`, "_blank") : undefined} data-testid={`crono-btn-VD-${idx}`}
                              title={`Visualizza contabile bonifico${it.contabile_filename ? ` (${it.contabile_filename})` : ""}`}
                              style={btnStyle(clr("#0288d1"), !!it.has_contabile)}>VD</button>
                            <button onClick={() => setStato(it, "scaduta", "Marcare come SCADUTA")} data-testid={`crono-btn-SC-${idx}`}
                              title="Marca come SCADUTA (>24h senza conferma)"
                              style={btnStyle(clr("#5d4037"), canScaduta)}>SC</button>
                            <button onClick={() => setLogAlunno(it.alunno_cod)} data-testid={`crono-btn-ST-${idx}`}
                              title={`Storico iscrizioni extra di ${it.cognome} ${it.nome} — apri per Cambio corso / variazione giorni`}
                              style={{ ...btnStyle("#6a1b9a", true), width: 40, boxShadow: "0 1px 3px rgba(106,27,154,0.5)" }}>ST</button>
                            <button onClick={() => setRecessoModal({ it, value: it.data_recesso || "" })} data-testid={`crono-btn-R-${idx}`}
                              title={isRecesso ? `Recesso dal ${it.data_recesso} — click per modificare/rimuovere` : "Imposta data recesso"}
                              style={btnStyle(clr(isRecesso ? "#c62828" : "#78909c"), true)}>R</button>
                            <button onClick={() => eliminaIscr(it)} data-testid={`crono-btn-X-${idx}`}
                              title="Elimina iscrizione (irreversibile)"
                              style={btnStyle(clr("#c62828"), true)}>X</button>
                          </span>
                        </td>
                      </>
                    );
                  })()}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {mailModalIscr && mailModalTpl && (
        <MailTemplateModal iscrizione={mailModalIscr} templateId={mailModalTpl}
          onClose={chiudiMailModal}
          onSent={() => { chiudiMailModal(); load(); }} />
      )}
      {movimentiModal && (
        <MovimentiCollegatiModal data={movimentiModal} onClose={() => setMovimentiModal(null)} />
      )}
      {logAlunno && (
        <AlunnoLogModal alunnoCod={logAlunno} onClose={() => setLogAlunno(null)} />
      )}
      {recessoModal && (
        <div onClick={() => setRecessoModal(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 750, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()} data-testid="crono-recesso-modal"
            style={{ background: "white", width: "100%", maxWidth: 420, borderRadius: 6, overflow: "hidden" }}>
            <div style={{ background: "#c62828", color: "white", padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <strong>⊘ Data recesso · {recessoModal.it.cognome} {recessoModal.it.nome}</strong>
              <button onClick={() => setRecessoModal(null)} data-testid="crono-recesso-close" style={{ background: "none", border: "none", color: "white", cursor: "pointer", fontSize: 18 }}>✕</button>
            </div>
            <div style={{ padding: 18 }}>
              <div style={{ fontSize: 11, color: "#666", marginBottom: 10 }}>
                Corso: <b>{recessoModal.it.corso_descrizione || recessoModal.it.corso_codice || "—"}</b><br/>
                Oltre questa data l&apos;iscrizione viene esclusa dalla generazione mensa e dalla graduatoria.
              </div>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#666", display: "block", marginBottom: 4 }}>DATA RECESSO</label>
              <input type="date" value={recessoModal.value || ""}
                onChange={e => setRecessoModal({ ...recessoModal, value: e.target.value })}
                data-testid="crono-recesso-input"
                style={{ width: "100%", padding: 8, border: recessoModal.value ? "2px solid #c62828" : "1px solid #ccc", background: recessoModal.value ? "#ffebee" : "white", fontSize: 13, fontFamily: "Consolas, monospace", color: recessoModal.value ? "#c62828" : "#333", fontWeight: 700, borderRadius: 3 }} />
              <div style={{ marginTop: 6 }}>
                <button onClick={() => setRecessoModal({ ...recessoModal, value: new Date().toISOString().slice(0, 10) })}
                  data-testid="crono-recesso-today"
                  style={{ padding: "4px 10px", background: "#eeeeee", border: "1px solid #bdbdbd", fontSize: 11, cursor: "pointer", marginRight: 6 }}>Oggi</button>
                {recessoModal.value && (
                  <button onClick={() => setRecessoModal({ ...recessoModal, value: "" })}
                    data-testid="crono-recesso-clear"
                    style={{ padding: "4px 10px", background: "#fff3e0", border: "1px solid #ffb74d", fontSize: 11, cursor: "pointer", color: "#bf360c" }}>Pulisci (rimuovi recesso)</button>
                )}
              </div>
            </div>
            <div style={{ padding: "12px 18px", background: "#f5f5f5", display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button onClick={() => setRecessoModal(null)} data-testid="crono-recesso-cancel"
                style={{ padding: "8px 16px", background: "white", border: "1px solid #ccc", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Annulla</button>
              <button onClick={async () => {
                const it = recessoModal.it;
                const nv = recessoModal.value || "";
                try {
                  await api.put(`/mensa-matrice/extra-iscrizioni/${it._id}`, { data_recesso: nv });
                  setRecessoModal(null);
                  await load();
                } catch (ex) { setErr(ex.response?.data?.detail || "Errore salvataggio data recesso"); }
              }}
                data-testid="crono-recesso-save"
                style={{ padding: "8px 16px", background: "#c62828", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Salva</button>
            </div>
          </div>
        </div>
      )}
      {loadingMov && !movimentiModal && (
        <div style={{ position: "fixed", top: 12, right: 12, background: "#1565c0", color: "white", padding: "6px 14px", borderRadius: 3, fontSize: 12, zIndex: 9999 }}>⏳ Caricamento movimenti…</div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: "#555", marginBottom: 3, letterSpacing: 0.5 }}>{label}</div>
      {children}
    </div>
  );
}

const th = { padding: "8px 10px", textAlign: "left", fontSize: 11, fontWeight: 700, letterSpacing: 0.5, borderBottom: "2px solid #263238" };
const td = { padding: "6px 10px", verticalAlign: "top" };
